import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { regStatus, editionMonthYear } from "@/lib/useEditions";
import Reveal from "@/components/shared/Reveal";
import Seo from "@/components/shared/Seo";
import Btn from "@/components/shared/Btn";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PROFILES } from "@/lib/constants";

const Edition2028 = () => {
  const { data: editions = [] } = useQuery({ queryKey: ["editions"], queryFn: async () => (await api.get("/editions")).data });
  const ed = editions.find((e) => e.status === "upcoming");
  const label = ed?.date_label || "la prochaine édition";
  const status = regStatus(ed);
  const my = editionMonthYear(ed);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", function: "", profile_type: "Architecte", profile_other: "", gdpr_consent: false });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) return toast.error("Merci d'accepter le traitement de vos données (RGPD).");
    setSending(true);
    try {
      await api.post("/registrations", { ...form, edition_id: ed?.id, interest_only: true });
      toast.success("C'est noté ! Vous serez informé en priorité de la prochaine édition.");
      setDone(true);
    } catch { toast.error("Une erreur est survenue."); }
    setSending(false);
  };

  return (
    <>
      <Seo title={`Prochaine édition (${label}) — FACADES2build`} description={`Soyez informé en priorité de la programmation de la prochaine édition de FACADES2build (${label}).`} />
      <section className="relative min-h-[45vh] flex items-end bg-f2b-black">
        {mediaUrl(ed?.hero_image) && <img src={mediaUrl(ed.hero_image)} alt="Prochaine édition" className="absolute inset-0 w-full h-full object-cover opacity-40" />}
        <div className="absolute inset-0 bg-gradient-to-t from-f2b-black via-f2b-black/70 to-transparent" />
        <div className="f2b-container relative z-10 pb-14 pt-40">
          <Reveal><p className="f2b-kicker text-f2b-cyan mb-4">Prochaine édition</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-display font-bold text-white text-4xl md:text-6xl leading-tight">{ed?.date_label || "Prochaine édition"}</h1></Reveal>
          <Reveal delay={0.16}><p className="text-white/70 mt-5 max-w-2xl">{ed?.summary || "Inscrivez-vous pour être informé en priorité de la prochaine édition de FACADES2build."}</p></Reveal>
        </div>
      </section>

      <section className="py-20 bg-f2b-light">
        <div className="f2b-container max-w-xl">
          {status === "ouvert" ? (
            <Reveal className="bg-white border border-f2b-filet p-10 text-center" data-testid="upcoming-open">
              <p className="f2b-kicker text-f2b-cyan mb-3">Inscriptions ouvertes</p>
              <h2 className="font-display font-bold text-f2b-black text-3xl mb-3">Réservez votre place pour {my ? `l'édition ${my}` : "la prochaine édition"}</h2>
              <p className="text-f2b-gray mb-6">Accès gratuit sur validation. Vous recevrez un billet nominatif avec QR code après confirmation.</p>
              {ed?.registration_url && /^https?:/i.test(ed.registration_url) ? (
                <a href={ed.registration_url} target="_blank" rel="noreferrer"><Btn variant="cyan" data-testid="upcoming-inscription-cta">S'inscrire – {my} <ArrowRight size={18} /></Btn></a>
              ) : (
                <Link to={ed?.registration_url || "/inscription"}><Btn variant="cyan" data-testid="upcoming-inscription-cta">S'inscrire – {my} <ArrowRight size={18} /></Btn></Link>
              )}
            </Reveal>
          ) : status === "ferme" ? (
            <Reveal className="bg-white border border-f2b-filet p-10 text-center" data-testid="upcoming-closed">
              <p className="f2b-kicker text-f2b-gray mb-3">Inscriptions</p>
              <h2 className="font-display font-bold text-f2b-black text-2xl">Inscriptions closes</h2>
              <p className="text-f2b-gray mt-3">{ed?.registration_closed_message || "Les inscriptions pour cette édition sont closes."}</p>
            </Reveal>
          ) : done ? (
            <Reveal className="bg-white border border-f2b-filet p-10 text-center">
              <h2 className="font-display font-bold text-f2b-black text-2xl">Merci !</h2>
              <p className="text-f2b-gray mt-3">Votre intérêt a bien été enregistré. Un e-mail de confirmation vous a été envoyé. Vous serez recontacté(e) en priorité dès l'ouverture de la programmation.</p>
            </Reveal>
          ) : (
            <Reveal>
              <h2 className="font-display font-bold text-f2b-black text-3xl mb-2">Être informé de {ed?.date_label ? `l'édition ${label}` : "la prochaine édition"}</h2>
              <p className="text-f2b-gray mb-8">Aucun engagement — simplement l'assurance d'être prévenu en priorité.</p>
              <form onSubmit={submit} className="bg-white p-8 border border-f2b-filet space-y-4" data-testid="interest-form">
                <div className="grid grid-cols-2 gap-4">
                  <Input required placeholder="Prénom *" value={form.first_name} onChange={set("first_name")} data-testid="interest-firstname" />
                  <Input required placeholder="Nom *" value={form.last_name} onChange={set("last_name")} data-testid="interest-lastname" />
                </div>
                <Input required type="email" placeholder="E-mail *" value={form.email} onChange={set("email")} data-testid="interest-email" />
                <Input placeholder="Téléphone (optionnel)" value={form.phone} onChange={set("phone")} data-testid="interest-phone" />
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="Société" value={form.company} onChange={set("company")} data-testid="interest-company" />
                  <Input placeholder="Fonction" value={form.function} onChange={set("function")} data-testid="interest-function" />
                </div>
                <Select value={form.profile_type} onValueChange={(v) => setForm((f) => ({ ...f, profile_type: v }))}>
                  <SelectTrigger data-testid="interest-profile"><SelectValue placeholder="Type de profil" /></SelectTrigger>
                  <SelectContent>{PROFILES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
                <label className="flex items-start gap-3 text-sm text-f2b-gray">
                  <Checkbox checked={form.gdpr_consent} onCheckedChange={(v) => setForm((f) => ({ ...f, gdpr_consent: !!v }))} data-testid="interest-gdpr" />
                  <span>J'accepte d'être contacté(e) au sujet de la prochaine édition de FACADES2build (RGPD).</span>
                </label>
                <Btn type="submit" variant="cyan" className="w-full" disabled={sending} data-testid="interest-submit">{sending ? "Envoi…" : `M'informer de ${ed?.date_label ? "l'édition " + label : "la prochaine édition"}`}</Btn>
              </form>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
};

export default Edition2028;
